import { SBRM, SBRMForm } from '@supplement-bacon/sbrm-react'
import { FormInstance } from 'antd'
import { forwardRef } from 'react'
import {
  createSegment,
  deleteSegment,
  updateSegment,
} from '../../../store/SegmentReducer'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { buildCriteriaPayload, validateSegmentForm } from '../CriteriaConfig'
import { SegmentFormValues } from '../types'
import Create from './Create'
import Delete from './Delete'
import Update from './Update'

export const SegmentForm: SBRMForm = forwardRef(({ formProps }, ref) => {
  const dispatch = useAppDispatch()
  const { error } = useAppSelector((state) => state.segment)

  return (
    <SBRM.BaseEntityForm
      {...{ ref, formProps }}
      error={error && String(error)}
      formActions={{
        create: Create,
        update: Update,
        delete: Delete,
      }}
      handlers={{
        // SBRM hands the FormInstance as the second argument
        // (`sbrm-react/dist/index.d.ts:257`). The validated `values` only
        // carry the mounted fields, so building the tree from them would
        // silently drop every collapsed criterion on save.
        create: async (values: SegmentFormValues, form: FormInstance) => {
          const stored = form.getFieldsValue(true) as SegmentFormValues
          const criteria = buildCriteriaPayload(stored)

          // A collapsed criterion never runs its field rules, so its bounds
          // are checked here on the whole store.
          const errors = validateSegmentForm(stored)
          if (errors.length > 0) {
            return Promise.reject(new Error(errors[0]))
          }

          // BaseEntityForm keeps the form open only when the handler rejects,
          // and SBRM surfaces its standard error toast: reject to block a
          // segment with no criterion without adding a second custom toast.
          if (criteria.children.length === 0) {
            return Promise.reject(new Error('segment.criteria_required'))
          }

          return dispatch(
            createSegment({
              title: (values.name ?? '').trim(),
              description: values.description?.trim(),
              icon: values.icon,
              color: values.color,
              criteria,
            } as any)
          ).then(() => true)
        },
        update: async (
          values: SegmentFormValues & { id: number },
          form: FormInstance
        ) => {
          // Same reason as create: the collapsed criteria live in the store
          // only, rules included.
          const stored = form.getFieldsValue(true) as SegmentFormValues
          const criteria = buildCriteriaPayload(stored)

          const errors = validateSegmentForm(stored)
          if (errors.length > 0) {
            return Promise.reject(new Error(errors[0]))
          }

          // Same guard as create: block an empty tree (the API would reject it
          // anyway) and let SBRM show its standard error toast.
          if (criteria.children.length === 0) {
            return Promise.reject(new Error('segment.criteria_required'))
          }

          return dispatch(
            updateSegment({
              id: values.id,
              title: (values.name ?? '').trim(),
              description: values.description?.trim(),
              icon: values.icon,
              color: values.color,
              criteria,
            } as any)
          ).then(() => true)
        },
        delete: async (values: any) =>
          dispatch(deleteSegment(values)).then(() => true),
      }}
    />
  )
})

SegmentForm.displayName = 'SegmentForm'

export default SegmentForm
